import React, { useContext } from 'react';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { MeasurementColors } from '../../types';
import ColorContext from '../../ColorContext';
import { SubjectResult } from '../../util/buildQueries';
import { useIntl } from 'react-intl';
import { getObservationMMMYYYYDate, getObservationUnit } from '../../util/fhir';
import { ValueBadge } from './ValueBadge';
import { messages } from './constants';

export const useStyles = makeStyles<{
  colors: MeasurementColors;
  variant?: 'inline';
}>((theme: Theme) => ({
  multipleReferenceRangeCell: {
    width: '100%',
    maxWidth: theme.spacing(50),
    flexDirection: 'column',
    maxHeight: '100%',
  },
  referenceRangeContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1fr',
    gap: theme.spacing(0.5),
    alignItems: 'flex-end',
    paddingBottom: ({ variant }) =>
      variant === 'inline' ? undefined : theme.spacing(0.5),
    width: '100%',
  },
  referenceRangeDate: {
    textTransform: 'uppercase',
    lineHeight: theme.pxToRem(16),
  },
  referenceRangeUnits: {
    gap: theme.spacing(1),
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  referenceRangeIndicatorContainer: {
    marginTop: theme.spacing(0.5),
    width: '100%',
    display: 'grid',
    gap: theme.spacing(0.5),
    gridTemplateColumns: '1fr 2fr 1fr',
  },
  /** Inline + no numeric range: flex row only — avoids empty grid columns (1fr/0fr/1fr) reserving space. */
  inlineNoRangeRow: {
    justifyContent: 'flex-end',
    minWidth: 0,
    width: '100%',
  },
  inlineNoRangeValue: {
    maxWidth: '100%',
    minWidth: 0,
  },
  unit: {
    color: theme.palette.text.hint,
    lineHeight: theme.pxToRem(16),
  },
  greyText: {
    color: '#9C9C9C',
    lineHeight: theme.pxToRem(16),
  },
  referenceRangeIndicator: {
    width: '100%',
    height: theme.spacing(0.75),
    borderRadius: theme.shape.borderRadius,
  },
  referenceRangeIndicatorDotContainer: {
    width: '100%',
    position: 'relative',
  },
  inlineRangeValueContainer: {
    minWidth: theme.spacing(8),
    marginLeft: theme.spacing(1),
  },
  referenceRangeIndicatorDot: {
    height: theme.pxToRem(18),
    width: theme.pxToRem(18),
    position: 'absolute',
    top: '50%',
    backgroundColor: ({ colors }) => colors.normal,
    borderColor: theme.palette.common.white,
    borderWidth: 4,
    borderStyle: 'solid',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
  },
  statusText: {
    width: '100%',
    textWrap: 'nowrap',
    color: '#9C9C9C',
  },
}));

export const MultiReferenceRange = (props: {
  result: SubjectResult;
  value: string | number | null | undefined;
  variant?: 'inline';
  colors?: Partial<MeasurementColors>;
}) => {
  const { result, value, variant } = props;
  const isInline = variant === 'inline';
  const colors = useContext(ColorContext);
  const colorsToUse = { ...colors, ...props.colors };
  const classes = useStyles({ colors: colorsToUse, variant });
  const { formatMessage } = useIntl();

  const low = result.ranges?.['in-range']?.lower;
  const high = result.ranges?.['in-range']?.upper;
  const units = getObservationUnit(result.resource);
  const isInRange = result.status === 'in-range';
  const isAttention = result.status === 'attention';
  const isPending = result.status === 'pending';
  const inRangeSpanAmount =
    1 + (typeof low !== 'number' ? 1 : 0) + (typeof high !== 'number' ? 1 : 0);

  let rangePercent: number | undefined;
  let isLow = false;
  let isHigh = false;

  if (typeof value === 'number') {
    rangePercent = 50;
    if (low !== undefined && high !== undefined) {
      rangePercent = ((value - low) / (high - low)) * 100;
    }
    isLow = value < (low ?? -Infinity);
    isHigh = value > (high ?? Infinity);
  }

  const hasRange = typeof low === 'number' || typeof high === 'number';
  const defaultMessage = isPending ? messages.pending : messages.view;
  let displayValue = value ?? formatMessage(defaultMessage);
  if (typeof value === 'number') {
    displayValue = Math.round(value * 100) / 100;
  }

  return (
    <Box fullWidth align="center" data-testid={`result-${result.resource.id}`}>
      <Box className={classes.multipleReferenceRangeCell}>
        {isInline && !hasRange ? (
          <Box
            className={classes.inlineNoRangeRow}
            direction="row"
            align="flex-end"
            flexWrap={true}
            gap={1}
            width="100%"
          >
            {units ? (
              <Text size="caption" className={classes.unit} data-testid="units">
                {units}
              </Text>
            ) : null}
            <Box className={classes.inlineNoRangeValue}>
              <ValueBadge
                status={result.status}
                value={displayValue}
                colors={colorsToUse}
              />
            </Box>
          </Box>
        ) : (
          <Box className={classes.referenceRangeContainer}>
            <Box direction="column" gap={0.25}>
              <Text
                size="subbody"
                className={classes.referenceRangeDate}
                data-testid="date"
              >
                {/* Inline sits beside the result picker, which already shows date/time. */}
                {!isInline && getObservationMMMYYYYDate(result.resource)}
              </Text>
              <Text size="caption" className={classes.unit} data-testid="units">
                {(!isInline || !hasRange) && units}
              </Text>
            </Box>

            <Box className={classes.referenceRangeUnits}>
              <Text
                size="caption"
                className={classes.greyText}
                data-testid="range-low"
              >
                {low}
              </Text>
              <Text
                size="caption"
                className={classes.greyText}
                data-testid="range-high"
              >
                {high}
              </Text>
            </Box>
            {(!isInline || !hasRange) && (
              <ValueBadge
                status={result.status}
                value={displayValue}
                colors={colorsToUse}
              />
            )}
          </Box>
        )}

        {hasRange && (
          <>
            <Box className={classes.referenceRangeIndicatorContainer}>
              {typeof low === 'number' && (
                <Box
                  className={classes.referenceRangeIndicator}
                  bgColor={isAttention && isLow ? colorsToUse.bad : '#D9D9D9'}
                  data-testid="low-indicator"
                />
              )}
              <Box
                className={classes.referenceRangeIndicator}
                bgColor={isInRange ? colorsToUse.normal : '#D9D9D9'}
                data-testid="in-range-indicator"
                paddingX={0.6}
                style={{
                  gridColumn: `span ${inRangeSpanAmount}`,
                }}
              >
                {typeof rangePercent === 'number' && isInRange && (
                  <Box className={classes.referenceRangeIndicatorDotContainer}>
                    <Box
                      className={classes.referenceRangeIndicatorDot}
                      data-testid="in-range-indicator-knob"
                      style={{ left: `${rangePercent}%` }}
                    />
                  </Box>
                )}
              </Box>
              {typeof high === 'number' && (
                <Box
                  className={classes.referenceRangeIndicator}
                  bgColor={isAttention && isHigh ? colorsToUse.bad : '#D9D9D9'}
                  data-testid="high-indicator"
                />
              )}
            </Box>

            <Box className={classes.referenceRangeIndicatorContainer}>
              {isInRange && (
                <Box
                  fullWidth
                  style={{
                    gridColumn: `${
                      typeof low === 'number' ? 2 : 1
                    } / span ${inRangeSpanAmount}`,
                  }}
                >
                  <Text
                    className={classes.statusText}
                    align="center"
                    size="caption"
                  >
                    {formatMessage(messages.inRange)}
                  </Text>
                </Box>
              )}
              {isAttention && (
                <Box
                  style={{
                    gridColumn: `${isHigh ? -1 : 1}`,
                  }}
                >
                  <Text
                    className={classes.statusText}
                    align={isHigh ? 'right' : 'left'}
                    size="caption"
                  >
                    {formatMessage(messages.outOfRange)}
                  </Text>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
      {isInline && hasRange && (
        <Box className={classes.inlineRangeValueContainer}>
          <ValueBadge
            status={result.status}
            value={displayValue}
            colors={colorsToUse}
          />
        </Box>
      )}
    </Box>
  );
};
